import React from 'react';
import { HelmetProvider } from 'react-helmet-async';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './app/App';
import reportWebVitals from './reportWebVitals';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { AuthProvider } from './context/AuthProvider';
import { LocalizationProvider } from './theme/localization-provider';
import { ThemeProvider } from './theme/theme-provider/theme-provider';
import ErrorPage from './components/support/ErrorPage';
import Signup from './components/pages/login/Signup';
import ProtectedComponent from './app/ProtectedComponent';
import { Toaster } from 'react-hot-toast';
import { paths } from './utils/paths';

import DashboardLayout from './components/layout/dashboards/DashboardLayout';
import SignIn from './components/pages/login/sign-in';

import AdminHome from './components/pages/admin/AdminHome';
import AdminSettings from './components/pages/admin/settings/AdminSettings';
import AdminActivities from './components/pages/admin/AdminActivities';
import AdminBilling from './components/pages/admin/AdminBilling';
import AdminSubscriptions from './components/pages/admin/subscriptions/AdminSubscriptions';
import AdminProfile from './components/pages/admin/profile/AdminProfile';
import AdminBusiness from './components/pages/admin/business/AdminBusiness';
import AdminNewUser from './components/pages/admin/business/admin-user-create';
import AdminViewBusiness from './components/pages/admin/business/admin-user-view';
import AdminEditBusinessBase from './components/pages/common/business/BusinessEditBasicDetails';
import AdminCompany from './components/pages/admin/company/AdminCompany';
import AdminCreateCompany from './components/pages/admin/company/AdminCreateCompany';
import AdminViewCompany from './components/pages/admin/company/AdminViewCompany';
import AdminEditCompanyBase from './components/pages/admin/company/AdminEditCompanyBase';
import AdminAgent from './components/pages/admin/agent/AdminAgent';
import AdminAgentCreate from './components/pages/admin/agent/admin-agent-create';
import AdminAgentView from './components/pages/admin/agent/admin-agent-view';
import AdminAgentBusinessDetails from './components/pages/admin/agent/AdminAgentBusinessDetails';
import AdminWorkflowsView from './components/pages/admin/admin-workflows';
import AddAssessmentForm from './components/pages/admin/assessment/admin-add-assessment';
import ViewAssessments from './components/pages/admin/assessment/admin-view-assessments';
import ViewAssessmentDetails from './components/pages/admin/assessment/admin-view-assessment-details';
import ProgramForm from './components/pages/admin/programs/admin-new-program';
import WorkflowForm from './components/pages/admin/programs/admin-new-workflow';
import ViewProgram from './components/pages/admin/programs/admin-view-programs';
import ViewProgramDetails from './components/pages/admin/programs/admin-view-program-details';

import AgentHome from './components/pages/agent/AgentHome';
import AgentClientView from './components/pages/agent/clients/agent-clients';
import ClientDetailView from './components/pages/agent/clients/ClientDetailView';
import AgentSettings from './components/pages/agent/settings/AgentSettings';
import CompanyProfile from './components/pages/agent/company/CompanyProfile';
import AgentProfile from './components/pages/agent/AgentProfile';
import AgentAssessments from './components/pages/agent/assessment/AgentAssessments';
import AgentActivities from './components/pages/agent/AgentActivities';
import AgentWorkflows from './components/pages/agent/workflows/agent-workflows';
import AgentBilling from './components/pages/agent/AgentBilling';
import AgentSupport from './components/pages/agent/agent-support';
import CompanyEditServiceInfo from './components/pages/agent/company/CompanyEditServiceInfo';
import CompanyAgent from './components/pages/agent/company/agents/CompanyAgent';
import CompanyAgentView from './components/pages/agent/company/agents/CompanyAgentView';
import CompanyPlatformSubscription from './components/pages/agent/company/CompanyPlatformSubscription';
import CompanySubscriptionDetails from './components/pages/agent/company/CompanySubscriptionDetails';

import BusinessHome from './components/pages/business/BusinessHome';
import BusinessProfile from './components/pages/business/BusinessProfile';
import BusinessSettings from './components/pages/business/settings/BusinessSettings';
import BusinessSupport from './components/pages/business/BusinessSupport';
import BusinessSubscriptions from './components/pages/business/BusinessSubscriptions';
import BusinessWorkflowsView from './components/pages/business/business-workflows';
import BusinessAssessments from './components/pages/business/assessment/BusinessAssessments';
import BusinessPayments from './components/pages/business/BusinessPayments'; 
import BusinessEditCorporateProfile from './components/pages/common/business/BusinessEditCorporateProfile';

import AssessmentForm from './components/pages/common/assessment/AssessmentForm';
import AssessmentDetailView from './components/pages/common/assessment/AssessmentDetailView';
import SubscribeWorkflow from './components/pages/common/workflow/SubscribeWorkflow';
import WorkflowView from './components/pages/common/workflow/WorkflowView';

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorPage />,
  },
  {
    path: paths.auth.signIn,
    element: <SignIn />,
    errorElement: <ErrorPage />,
  },
  {
    path: paths.auth.signup,
    element: <Signup />,
    errorElement: <ErrorPage />,
  },
  {
    path: "/admin",
    element: <ProtectedComponent><DashboardLayout /></ProtectedComponent>,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "home",
        element: <AdminHome />
      },
      {
        path: "users",
        element: <AdminBusiness />
      },
      {
        path: "users/new",
        element: <AdminNewUser />
      },
      {
        path: "users/view",
        element: <AdminViewBusiness />
      },
      {
        path: "users/editbase",
        element: <AdminEditBusinessBase />
      },
      {
        path: "companies",
        element: <AdminCompany />
      },
      {
        path: "companies/new",
        element: <AdminCreateCompany />
      },
      {
        path: "companies/view",
        element: <AdminViewCompany />
      },
      {
        path: "companies/editbase",
        element: <AdminEditCompanyBase />
      },
      {
        path: "agents",
        element: <AdminAgent />
      },
      {
        path: "agents/new",
        element: <AdminAgentCreate />
      },
      {
        path: "agents/view",
        element: <AdminAgentView />
      },
      {
        path: "agents/business/view",
        element: <AdminAgentBusinessDetails />
      },
      {
        path: "assessments",
        element: <ViewAssessments />
      },
      {
        path: "assessments/new",
        element: <AddAssessmentForm />
      },
      {
        path: "assessments/update",
        element: <AddAssessmentForm />
      },
      {
        path: "assessments/details",
        element: <ViewAssessmentDetails />
      },
      {
        path: "assessment/form",
        element: <AssessmentForm userType="ADMIN" />
      },
      {
        path: "assessment/details",
        element: <AssessmentDetailView userType="ADMIN" />
      },
      {
        path: "programs",
        element: <ViewProgram />
      },
      {
        path: "programs/update",
        element: <ProgramForm />
      },
      {
        path: "programs/details",
        element: <ViewProgramDetails />
      },
      {
        path: "workflows/new",
        element: <WorkflowForm />
      },
      {
        path: "activities",
        element: <AdminActivities />
      },
      {
        path: "payments",
        element: <AdminBilling />
      },
      {
        path: "subscriptions",
        element: <AdminSubscriptions />
      },
      {
        path: "profile",
        element: <AdminProfile />
      },
      {
        path: "settings",
        element: <AdminSettings />
      },
      {
        path: "workflows",
        element: <AdminWorkflowsView />
      },
      {
        path: "workflow/subscribe",
        element: <SubscribeWorkflow />
      },
      {
        path: "workflow/view",
        element: <WorkflowView />
      }
    ]
  },
  {
    path: "/business",
    element: <ProtectedComponent><DashboardLayout /></ProtectedComponent>,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "home",
        element: <BusinessHome />
      },
      {
        path: "profile",
        element: <BusinessProfile />
      },
      {
        path: "profile/edit",
        element: <BusinessEditCorporateProfile />
      },
      {
        path: "settings",
        element: <BusinessSettings />
      },
      {
        path: "support",
        element: <BusinessSupport />
      },
      {
        path: "subscriptions",
        element: <BusinessSubscriptions />
      },
      {
        path: "payments",
        element: <BusinessPayments />
      },
      {
        path: "workflows",
        element: <BusinessWorkflowsView />
      },
      {
        path: "assessment/view",
        element: <BusinessAssessments />
      },
      {
        path: "assessment/form",
        element: <AssessmentForm userType="BUSINESS" />
      },
      {
        path: "assessment/details",
        element: <AssessmentDetailView userType="BUSINESS" />
      },
      {
        path: "workflow/subscribe",
        element: <SubscribeWorkflow />
      },
      {
        path: "workflow/view",
        element: <WorkflowView />
      }
    ]
  },
  {
    path: "/agent",
    element: <ProtectedComponent><DashboardLayout /></ProtectedComponent>,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "home",
        element: <AgentHome />
      },
      {
        path: "clients",
        element: <AgentClientView />
      },
      {
        path: "clients/view",
        element: <ClientDetailView />
      },
      {
        path: "workflows",
        element: <AgentWorkflows />
      },
      {
        path: "assessment/view",
        element: <AgentAssessments />
      },
      {
        path: "activities",
        element: <AgentActivities />
      },
      {
        path: "company/profile",
        element: <CompanyProfile />
      },
      {
        path: "company/editServiceInfo",
        element: <CompanyEditServiceInfo />
      },
      {
        path: "company/agents",
        element: <CompanyAgent />
      },
      {
        path: "company/agents/view",
        element: <CompanyAgentView />
      },
      {
        path: "company/platformSubscriptions",
        element: <CompanyPlatformSubscription />
      },
      {
        path: "company/subscriptionDetails",
        element: <CompanySubscriptionDetails />
      },
      {
        path: "profile",
        element: <AgentProfile />
      },
      {
        path: "settings",
        element: <AgentSettings />
      },
      {
        path: "payments",
        element: <AgentBilling />
      },
      {
        path: "support",
        element: <AgentSupport />
      },
      {
        path: "assessment/view",
        element: <AgentAssessments />
      },
      {
        path: "assessment/form",
        element: <AssessmentForm userType="AGENT" />
      },
      {
        path: "assessment/details",
        element: <AssessmentDetailView userType="AGENT" />
      },
      {
        path: "workflow/subscribe",
        element: <SubscribeWorkflow />
      },
      {
        path: "workflow/view",
        element: <WorkflowView />
      },
      {
        path: "workflow/subscribe",
        element: <SubscribeWorkflow />
      },
    ]
  }
])

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <HelmetProvider>
      <AuthProvider>
        <LocalizationProvider>
          <ThemeProvider>
            <RouterProvider router = {router} />
          </ThemeProvider>
        </LocalizationProvider>
        <Toaster />
      </AuthProvider>
    </HelmetProvider>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
